import { computed, inject } from '@angular/core';

import { updateState, withDevtools, withDevToolsStub, withGlitchTracking } from '@angular-architects/ngrx-toolkit';
import { signalStore, withComputed, withMethods, withProps, withState } from '@ngrx/signals';

import { ConfigService } from '../services/config/config.service';
import { TaskLogService } from '../services/task/task-log.service';
import { FarmStore } from './farm.store';
import { TaskLog } from '../entities/task/task-log.entities';
import {
    TaskLogQueryParams,
    TaskLogFilters,
    TaskLogCreateRequest,
    TaskLogUpdateRequest,
    TaskLogStatistics
} from '../entities/task/task-log-api.types';
import { withCondition } from './utils/with-condition';

interface TaskLogState {
    taskLogs: TaskLog[];
    selectedTaskLog: TaskLog | null;
    loading: boolean;
    error: string | null;
    totalTaskLogs: number;
    currentPage: number;
    pageSize: number;
    hasMoreTaskLogs: boolean;
    searchQuery: string;
    filters: TaskLogFilters;
    taskLogStatistics: TaskLogStatistics | null;
}

const initialState: TaskLogState = {
    taskLogs: [],
    selectedTaskLog: null,
    loading: false,
    error: null,
    totalTaskLogs: 0,
    currentPage: 0,
    pageSize: 100,
    hasMoreTaskLogs: false,
    searchQuery: '',
    filters: {},
    taskLogStatistics: null,
};

export const TaskLogStore = signalStore(
    withState(initialState),
    withProps(() => ({
        configService: inject(ConfigService),
        taskLogService: inject(TaskLogService),
        farmStore: inject(FarmStore),
    })),
    withComputed(({ taskLogs, selectedTaskLog, loading, error, totalTaskLogs, hasMoreTaskLogs, taskLogStatistics }) => ({
        hasTaskLogs: computed(() => taskLogs().length > 0),
        taskLogsCount: computed(() => taskLogs().length),
        totalTaskLogsCount: computed(() => totalTaskLogs()),
        isLoading: computed(() => loading()),
        hasError: computed(() => error() !== null),
        selectedTaskLogAction: computed(() => selectedTaskLog()?.action || null),
        hasMoreTaskLogsAvailable: computed(() => hasMoreTaskLogs()),
        hasTaskLogStatistics: computed(() => taskLogStatistics() !== null),
    })),
    withCondition(({ configService }) => !configService?.environment?.production,
        withDevtools('task-log', withGlitchTracking()),
        withDevToolsStub('task-log'),
    ),
    withMethods((store) => ({
        loadTaskLogs(params?: TaskLogQueryParams, filters?: TaskLogFilters, append = false): void {
            updateState(store, '[TaskLog] Load Task Logs Start', { loading: true, error: null });

            store.taskLogService.getLogs(params, filters).subscribe({
                next: (response) => {
                    const newTaskLogs = append ? [...store.taskLogs(), ...response.data] : response.data;
                    updateState(store, '[TaskLog] Load Task Logs Success', {
                        taskLogs: newTaskLogs,
                        totalTaskLogs: response.total,
                        currentPage: params?.skip ? Math.floor((params.skip + response.data.length) / (params?.limit || 100)) : 0,
                        pageSize: params?.limit || 100,
                        hasMoreTaskLogs: (params?.skip || 0) + response.data.length < response.total,
                        loading: false,
                        error: null,
                        filters: filters || {},
                        searchQuery: ''
                    });
                },
                error: (error) => {
                    updateState(store, '[TaskLog] Load Task Logs Error', {
                        loading: false,
                        error: error.message || 'Failed to load task logs'
                    });
                }
            });
        },

        searchTaskLogs(query: string, params?: Omit<TaskLogQueryParams, 'q'>): void {
            updateState(store, '[TaskLog] Search Task Logs Start', { loading: true, error: null, searchQuery: query });

            const currentFarmId = store.farmStore.currentFarm()?.id;
            const searchParams = { ...params, q: query };
            const farmFilters = {
                ...(currentFarmId && { farm_id: currentFarmId })
            };

            store.taskLogService.getLogs(searchParams, farmFilters).subscribe({
                next: (response) => {
                    updateState(store, '[TaskLog] Search Task Logs Success', {
                        taskLogs: response.data,
                        totalTaskLogs: response.total,
                        currentPage: 0,
                        hasMoreTaskLogs: response.data.length < response.total,
                        loading: false,
                        error: null,
                        searchQuery: query
                    });
                },
                error: (error) => {
                    updateState(store, '[TaskLog] Search Task Logs Error', {
                        loading: false,
                        error: error.message || 'Failed to search task logs'
                    });
                }
            });
        },

        loadTaskLogById(id: string): void {
            updateState(store, '[TaskLog] Load Task Log By ID Start', { loading: true, error: null });

            const farmId = store.farmStore.currentFarm()?.id;
            if (!farmId) {
                updateState(store, '[Task Log] Load Log By ID Error', {
                    loading: false,
                    error: 'Farm ID is required to get log'
                });
                return;
            }

            store.taskLogService.getLogById(id, farmId).subscribe({
                next: (taskLog) => {
                    const existingIndex = store.taskLogs().findIndex(tl => tl.id === id);
                    const newTaskLogs = [...store.taskLogs()];
                    if (existingIndex >= 0) {
                        newTaskLogs[existingIndex] = taskLog;
                    } else {
                        newTaskLogs.push(taskLog);
                    }

                    updateState(store, '[TaskLog] Load Task Log By ID Success', {
                        taskLogs: newTaskLogs,
                        loading: false,
                        error: null
                    });
                },
                error: (error) => {
                    updateState(store, '[TaskLog] Load Task Log By ID Error', {
                        loading: false,
                        error: error.message || 'Failed to load task log'
                    });
                }
            });
        },

        createTaskLog(taskLogData: TaskLogCreateRequest): void {
            updateState(store, '[TaskLog] Create Task Log Start', { loading: true, error: null });

            const farmId = store.farmStore.currentFarm()?.id;
            if (!farmId) {
                updateState(store, '[Task Log] Create Log Error', {
                    loading: false,
                    error: 'Farm ID is required to create log'
                });
                return;
            }

            store.taskLogService.createLog(taskLogData, farmId).subscribe({
                next: (newTaskLog) => {
                    updateState(store, '[TaskLog] Create Task Log Success', {
                        taskLogs: [...store.taskLogs(), newTaskLog],
                        totalTaskLogs: store.totalTaskLogs() + 1,
                        loading: false,
                        error: null
                    });
                },
                error: (error) => {
                    updateState(store, '[TaskLog] Create Task Log Error', {
                        loading: false,
                        error: error.message || 'Failed to create task log'
                    });
                }
            });
        },

        updateTaskLog(id: string, updates: TaskLogUpdateRequest): void {
            updateState(store, '[TaskLog] Update Task Log Start', { loading: true, error: null });

            const farmId = store.farmStore.currentFarm()?.id;
            if (!farmId) {
                updateState(store, '[Task Log] Update Log Error', {
                    loading: false,
                    error: 'Farm ID is required to update log'
                });
                return;
            }

            store.taskLogService.updateLog(id, updates, farmId).subscribe({
                next: (updatedTaskLog) => {
                    const newTaskLogs = store.taskLogs().map(taskLog =>
                        taskLog.id === id ? updatedTaskLog : taskLog
                    );

                    updateState(store, '[TaskLog] Update Task Log Success', {
                        taskLogs: newTaskLogs,
                        loading: false,
                        error: null
                    });

                    if (store.selectedTaskLog()?.id === id) {
                        updateState(store, '[TaskLog] Update Selected Task Log', { selectedTaskLog: updatedTaskLog });
                    }
                },
                error: (error) => {
                    updateState(store, '[TaskLog] Update Task Log Error', {
                        loading: false,
                        error: error.message || 'Failed to update task log'
                    });
                }
            });
        },

        deleteTaskLog(id: string): void {
            updateState(store, '[TaskLog] Delete Task Log Start', { loading: true, error: null });

            const farmId = store.farmStore.currentFarm()?.id;
            if (!farmId) {
                updateState(store, '[Task Log] Delete Log Error', {
                    loading: false,
                    error: 'Farm ID is required to delete log'
                });
                return;
            }

            store.taskLogService.deleteLog(id, farmId).subscribe({
                next: () => {
                    const newTaskLogs = store.taskLogs().filter(taskLog => taskLog.id !== id);
                    updateState(store, '[TaskLog] Delete Task Log Success', {
                        taskLogs: newTaskLogs,
                        totalTaskLogs: Math.max(0, store.totalTaskLogs() - 1),
                        loading: false,
                        error: null
                    });

                    if (store.selectedTaskLog()?.id === id) {
                        updateState(store, '[TaskLog] Clear Selected Task Log', { selectedTaskLog: null });
                    }
                },
                error: (error) => {
                    updateState(store, '[TaskLog] Delete Task Log Error', {
                        loading: false,
                        error: error.message || 'Failed to delete task log'
                    });
                }
            });
        },

        setTaskLogsData(taskLogs: TaskLog[]): void {
            updateState(store, '[TaskLog] Set Task Logs Data', { taskLogs, loading: false, error: null });
        },

        setLoading(loading: boolean): void {
            updateState(store, '[TaskLog] Set Loading', { loading });
        },

        selectTaskLog(taskLog: TaskLog | null): void {
            updateState(store, '[TaskLog] Select Task Log', { selectedTaskLog: taskLog });
        },

        selectTaskLogById(taskLogId: string): void {
            const taskLog = store.taskLogs().find(tl => tl.id === taskLogId) || null;
            this.selectTaskLog(taskLog);
        },

        loadNextPage(): void {
            if (!store.hasMoreTaskLogs()) return;

            const skip = store.currentPage() * store.pageSize();
            const params: TaskLogQueryParams = {
                skip,
                limit: store.pageSize()
            };

            this.loadTaskLogs(params, store.filters(), true);
        },

        setFilters(filters: TaskLogFilters): void {
            updateState(store, '[TaskLog] Set Filters', { filters });
            this.loadTaskLogs(undefined, filters);
        },

        clearFilters(): void {
            updateState(store, '[TaskLog] Clear Filters', { filters: {}, searchQuery: '' });
            this.loadTaskLogs();
        },

        refreshTaskLogs(): void {
            this.loadTaskLogs();
        },

        clearError(): void {
            updateState(store, '[TaskLog] Clear Error', { error: null });
        },

        reset(): void {
            updateState(store, '[TaskLog] Reset', initialState);
        },

        getTaskLogById(id: string): TaskLog | undefined {
            return store.taskLogs().find(taskLog => taskLog.id === id);
        },

        hasAccessToTaskLog(taskLogId: string): boolean {
            return store.taskLogs().some(taskLog => taskLog.id === taskLogId);
        }
    }))
);
